import { useState, useRef, useMemo, useCallback } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  X,
  Send,
  Clock,
  Trash2,
  Loader2,
  Upload,
  Sparkles,
  Camera,
  Tag,
  ChevronDown,
  ChevronUp,
  Swords,
  Eye,
  EyeOff,
  RefreshCw,
} from "lucide-react";
import { trackEvent } from "@/hooks/useTracking";
import { DD, POST_TYPES, CTA_TYPES } from "./constants";
import { computeKwScore } from "./utils";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { ACCEPT_IMAGES, isImageFile } from "@/lib/image-files";
import { useToast } from "@/hooks/use-toast";
import type { Location, Post, Keyword } from "@shared/schema";
import type { CompetitorPressure, CompetitorInfo, ImageGenerateResponse, PostSavePayload } from "./types";
import { getPostTags } from "./types";

export function PostEditor({
  locationId,
  location,
  keywords,
  onClose,
  editPost,
  onPublishSuccess,
  onScheduled,
  initialDate,
}: {
  locationId: string;
  location?: Location;
  keywords: Keyword[];
  onClose: () => void;
  editPost?: Post | null;
  onPublishSuccess?: (kw?: string) => void;
  onScheduled?: (postId: string) => boolean;
  initialDate?: string | null;
}) {
  const { toast } = useToast();
  const [content, setContent] = useState(editPost?.content || "");
  const [postType, setPostType] = useState(editPost?.postType || "update");
  const [title, setTitle] = useState(editPost?.title || "");
  const [scheduledDate, setScheduledDate] = useState(
    editPost?.scheduledAt ? new Date(editPost.scheduledAt).toISOString().slice(0, 10) : (initialDate || new Date().toISOString().slice(0, 10))
  );
  const [scheduledTime, setScheduledTime] = useState(
    editPost?.scheduledAt ? new Date(editPost.scheduledAt).toTimeString().slice(0, 5) : "09:00"
  );
  const [ctaType, setCtaType] = useState(editPost?.ctaType || "LEARN_MORE");
  const [ctaUrl, setCtaUrl] = useState(editPost?.ctaUrl || location?.website || "");
  const [mediaUrl, setMediaUrl] = useState(editPost?.mediaUrl || "");
  const [couponCode, setCouponCode] = useState(editPost?.couponCode || "");
  const [eventStartDate, setEventStartDate] = useState(
    editPost?.eventStartDate ? new Date(editPost.eventStartDate).toISOString().slice(0, 10) : ""
  );
  const [eventEndDate, setEventEndDate] = useState(
    editPost?.eventEndDate ? new Date(editPost.eventEndDate).toISOString().slice(0, 10) : ""
  );
  const [tags, setTags] = useState<string[]>(editPost ? getPostTags(editPost) : []);
  const [tagInput, setTagInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [showCompetitorIntel, setShowCompetitorIntel] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [generatedPrompt, setGeneratedPrompt] = useState("");
  const [showPrompt, setShowPrompt] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const charCount = content.length;
  const maxChars = 1500;
  const kwScore = computeKwScore(content, keywords);
  const detectedKws = keywords.filter(k => content.toLowerCase().includes(k.keyword.toLowerCase()));
  const suggestedKws = keywords.filter(k => !detectedKws.includes(k)).slice(0, 4);

  const { data: pressure } = useQuery<CompetitorPressure>({
    queryKey: ["/api/competitor-pressure", locationId],
    enabled: !!locationId,
  });

  const entityProps = editPost?.entityProperties as Record<string, string | number | boolean> | null;

  const processFile = useCallback(async (file: File) => {
    if (!isImageFile(file)) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await fetch("/api/uploads", { method: "POST", body: formData, credentials: "include" });
      if (!res.ok) throw new Error("Upload failed");
      const { url } = await res.json();
      setMediaUrl(url);
    } catch {
      toast({ title: "Failed to upload image", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  }, [toast]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleGenerateImage = async (promptOverride?: string) => {
    setUploading(true);
    try {
      const endpoint = editPost?.id
        ? `/api/posts/${editPost.id}/generate-image`
        : `/api/posts/generate-image`;
      const payload: Record<string, string> = {
        locationId,
        content,
        postType,
      };
      if (promptOverride) {
        payload.customPrompt = promptOverride;
      }
      const res = await apiRequest("POST", endpoint, payload);
      const data: ImageGenerateResponse = await res.json();
      if (data.prompt) {
        setGeneratedPrompt(data.prompt);
        setCustomPrompt(data.prompt);
      }
      if (data.imageUrl) {
        setMediaUrl(data.imageUrl);
        toast({ title: "Image generated" });
      } else {
        toast({ title: "No image generated", description: data.message || "Image generation service unavailable", variant: "destructive" });
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Unknown error";
      toast({ title: "Image generation failed", description: message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const saveMutation = useMutation({
    mutationFn: async (targetStatus: string) => {
      const resolvedCtaType = ctaType && ctaType !== "none" ? ctaType : undefined;
      const resolvedCtaUrl = resolvedCtaType ? ctaUrl : undefined;
      const scheduledAt = targetStatus === "scheduled" || targetStatus === "published"
        ? new Date(`${scheduledDate}T${scheduledTime}`).toISOString()
        : undefined;

      const data: PostSavePayload = {
        locationId,
        title: title || undefined,
        content,
        postType,
        status: targetStatus,
        scheduledAt,
        couponCode: postType === "offer" ? couponCode : undefined,
        eventStartDate: postType === "event" && eventStartDate ? new Date(eventStartDate).toISOString() : undefined,
        eventEndDate: postType === "event" && eventEndDate ? new Date(eventEndDate).toISOString() : undefined,
        ctaType: resolvedCtaType,
        ctaUrl: resolvedCtaUrl,
        mediaUrl: mediaUrl || undefined,
        tags,
        approvalStatus: targetStatus === "scheduled" ? "approved" : (targetStatus === "review" ? "pending" : (targetStatus === "draft" ? "draft" : "approved")),
      };

      if (targetStatus === "published") {
        let gbpPublished = false;
        let gbpError: string | undefined;
        try {
          const pubRes = await apiRequest("POST", "/api/gbp/posts/publish", {
            locationId,
            postText: content,
            ctaType: resolvedCtaType,
            ctaUrl: resolvedCtaUrl,
            mediaUrl: mediaUrl || undefined,
            postType,
            couponCode: postType === "offer" ? couponCode : undefined,
            eventStartDate: (postType === "event" || postType === "offer") && eventStartDate ? new Date(eventStartDate).toISOString() : undefined,
            eventEndDate: (postType === "event" || postType === "offer") && eventEndDate ? new Date(eventEndDate).toISOString() : undefined,
            title: title || undefined,
          });
          const pubData = await pubRes.json();
          gbpPublished = pubData?.published === true;
          gbpError = pubData?.error;
        } catch (e: any) {
          gbpPublished = false;
          gbpError = e?.message || "Could not reach the publish endpoint.";
        }

        const saveData = {
          ...data,
          status: gbpPublished ? "published" : "scheduled",
          publishedAt: gbpPublished ? new Date().toISOString() : undefined,
          approvalStatus: "approved",
        };

        if (editPost) {
          await apiRequest("PATCH", `/api/posts/${editPost.id}`, saveData);
        } else {
          await apiRequest("POST", "/api/posts", saveData);
        }
        return { status: gbpPublished ? "published" : "published_local_only", error: gbpError };
      }

      if (editPost) {
        await apiRequest("PATCH", `/api/posts/${editPost.id}`, data);
      } else {
        await apiRequest("POST", "/api/posts", data);
      }
      return { status: targetStatus };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["/api/posts", locationId] });
      if (result.status === "published") {
        const topKw = detectedKws.length > 0 ? detectedKws[0].keyword : undefined;
        onPublishSuccess?.(topKw);
      } else if (result.status === "published_local_only") {
        toast({
          title: "Post saved — Google publish failed",
          description: result.error || "Google rejected the post. The post has been saved and scheduled, but was not published to your Google Business Profile.",
          variant: "destructive",
        });
      } else if (result.status === "scheduled" && editPost && onScheduled) {
        const advanced = onScheduled(editPost.id);
        if (advanced) {
          toast({ title: "Post scheduled", description: "Opened the next post for review." });
          return;
        }
        toast({ title: "Post scheduled", description: "You've reviewed every post — nice work!" });
      } else {
        toast({ title: result.status === "scheduled" ? "Post scheduled" : result.status === "review" ? "Submitted for review" : "Draft saved" });
      }
      onClose();
    },
    onError: (err: Error) => {
      toast({ title: "Failed to save post", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiRequest("DELETE", `/api/posts/${editPost!.id}`),
    onSuccess: () => {
      trackEvent('gbp_post_deleted', { post_id: editPost?.id });
      queryClient.invalidateQueries({ queryKey: ["/api/posts", locationId] });
      toast({ title: "Post deleted" });
      onClose();
    },
    onError: (err: Error) => toast({ title: "Failed to delete", description: err.message, variant: "destructive" }),
  });

  const optimizationTips = useMemo(() => {
    const tips: Array<{ text: string; good: boolean }> = [];
    if (charCount >= 100 && charCount <= 300) tips.push({ text: "Great post length", good: true });
    else if (charCount < 100) tips.push({ text: "Quick win: stretch to 150–300 characters for stronger signal", good: false });
    else if (charCount > 300 && charCount <= 1500) tips.push({ text: "Good length for engagement", good: true });
    if (detectedKws.length > 0) tips.push({ text: `${detectedKws.length} keyword${detectedKws.length > 1 ? "s" : ""} detected — locking in local ranking`, good: true });
    else tips.push({ text: "Quick win: drop in a focus keyword to lock in local ranking", good: false });
    if (mediaUrl) tips.push({ text: "Image attached — great for engagement", good: true });
    else tips.push({ text: "Quick win: add an image for ~2x engagement lift", good: false });
    if (ctaType && ctaType !== "none") tips.push({ text: "CTA button active — driving action", good: true });
    else tips.push({ text: "Quick win: add a CTA button to convert reads into action", good: false });
    if (kwScore >= 80) tips.push({ text: "Excellent keyword optimization!", good: true });
    return tips;
  }, [charCount, detectedKws.length, mediaUrl, ctaType, kwScore]);

  const competitors = pressure?.competitors || [];

  return (
    <div className="fixed inset-0 z-[100] flex justify-end" data-testid="post-editor-overlay">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full max-w-[520px] h-full flex flex-col overflow-hidden animate-in slide-in-from-right duration-300"
        style={{ background: "#0c1018", borderLeft: `1px solid ${DD.border}` }}
      >
        <div className="px-5 py-4 flex justify-between items-center flex-shrink-0" style={{ borderBottom: `1px solid ${DD.border}` }}>
          <h3 className="text-base font-bold text-white" data-testid="editor-title">{editPost ? "Edit Post" : "New Post"}</h3>
          <button className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-white/5" style={{ color: DD.textSecondary }} onClick={onClose} data-testid="button-close-editor">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest block mb-2" style={{ color: DD.textSecondary }}>Post Type</label>
            <div className="flex gap-2">
              {POST_TYPES.map(t => (
                <button
                  key={t.value}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                  style={postType === t.value
                    ? { background: `${t.color}18`, color: t.color, border: `1px solid ${t.color}40` }
                    : { background: "rgba(255,255,255,0.03)", color: DD.textSecondary, border: `1px solid ${DD.border}` }
                  }
                  onClick={() => setPostType(t.value)}
                  data-testid={`button-type-${t.value}`}
                >
                  {t.icon} {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest block mb-2" style={{ color: DD.accent }}>Creative Studio</label>
            <input ref={fileInputRef} type="file" accept={ACCEPT_IMAGES} onChange={handleImageUpload} className="hidden" data-testid="input-image-upload" />
            {mediaUrl ? (
              <div>
                <div className="relative rounded-xl overflow-hidden" style={{ border: `2px solid ${DD.accentBorder}` }}>
                  <img src={mediaUrl} alt="Post image" className="w-full aspect-video object-cover" data-testid="img-post-preview" />
                  <div className="absolute top-2 right-2 flex gap-1.5">
                    <button className="w-7 h-7 rounded-full bg-black/60 backdrop-blur text-white flex items-center justify-center hover:bg-black/80" onClick={() => handleGenerateImage()} disabled={uploading} data-testid="button-regenerate-image" title="Regenerate image">
                      {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    </button>
                    <button className="w-7 h-7 rounded-full bg-black/60 backdrop-blur text-white flex items-center justify-center hover:bg-black/80" onClick={() => setShowPrompt(!showPrompt)} data-testid="button-toggle-prompt" title="View/edit prompt">
                      {showPrompt ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                    <button className="w-7 h-7 rounded-full bg-red-500/80 text-white flex items-center justify-center hover:bg-red-500" onClick={() => { setMediaUrl(""); setGeneratedPrompt(""); setShowPrompt(false); }} data-testid="button-remove-image" title="Remove image">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {generatedPrompt && showPrompt && (
                  <div className="mt-2 rounded-lg p-3" style={{ background: "rgba(249,115,22,0.04)", border: `1px solid ${DD.accentBorder}` }}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: DD.accent }}>Image Prompt</span>
                      <button
                        className="text-[10px] font-medium px-2 py-0.5 rounded transition-colors hover:bg-white/5"
                        style={{ color: DD.textSecondary }}
                        onClick={() => setIsEditingPrompt(!isEditingPrompt)}
                        data-testid="button-edit-prompt"
                      >
                        {isEditingPrompt ? "Cancel" : "Edit"}
                      </button>
                    </div>
                    {isEditingPrompt ? (
                      <div className="space-y-2">
                        <Textarea
                          value={customPrompt}
                          onChange={e => setCustomPrompt(e.target.value)}
                          className="min-h-[80px] resize-none text-xs leading-relaxed"
                          style={{ background: "#0a0e16", border: `1px solid ${DD.border}`, color: DD.text }}
                          data-testid="input-custom-prompt"
                        />
                        <Button
                          size="sm"
                          className="text-white text-xs font-semibold h-7"
                          style={{ background: DD.accent }}
                          onClick={() => { handleGenerateImage(customPrompt); setIsEditingPrompt(false); }}
                          disabled={uploading || !customPrompt.trim()}
                          data-testid="button-regenerate-custom"
                        >
                          <Sparkles className="w-3 h-3 mr-1" /> Regenerate with Custom Prompt
                        </Button>
                      </div>
                    ) : (
                      <p className="text-[11px] leading-relaxed text-white/40" data-testid="text-generated-prompt">{generatedPrompt}</p>
                    )}
                  </div>
                )}
              </div>
            ) : uploading ? (
              <div
                className="rounded-xl flex flex-col items-center justify-center py-10"
                style={{
                  border: `2px dashed ${DD.accent}`,
                  background: "rgba(249,115,22,0.04)",
                }}
                data-testid="dropzone-generating"
              >
                <div className="relative w-16 h-16 mb-4">
                  <div
                    className="absolute inset-0 rounded-full animate-spin"
                    style={{
                      border: `3px solid ${DD.border}`,
                      borderTopColor: DD.accent,
                    }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 animate-pulse" style={{ color: DD.accent }} />
                  </div>
                </div>
                <p className="text-sm font-semibold mb-1" style={{ color: DD.accent }}>
                  Creating Your Visual
                </p>
                <p className="text-[11px] animate-pulse" style={{ color: DD.textTertiary }}>
                  AI is crafting a brand-matched image…
                </p>
                <div className="mt-4 w-48 h-1.5 rounded-full overflow-hidden" style={{ background: DD.border }}>
                  <div
                    className="h-full rounded-full"
                    style={{
                      background: `linear-gradient(90deg, ${DD.accent}, ${DD.purple})`,
                      animation: "shimmer-bar 2.5s ease-in-out infinite",
                    }}
                  />
                </div>
                <style>{`
                  @keyframes shimmer-bar {
                    0% { width: 5%; }
                    50% { width: 75%; }
                    100% { width: 95%; }
                  }
                `}</style>
              </div>
            ) : (
              <div
                className={`rounded-xl flex flex-col items-center justify-center py-10 transition-all cursor-pointer ${isDragOver ? "scale-[1.01]" : ""}`}
                style={{
                  border: `2px dashed ${isDragOver ? DD.accent : DD.accentBorder}`,
                  background: isDragOver ? "rgba(249,115,22,0.06)" : "rgba(249,115,22,0.02)",
                }}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleDrop}
                data-testid="dropzone-image"
              >
                <Camera className="w-10 h-10 mb-3" style={{ color: DD.accent }} />
                <p className="text-sm font-semibold mb-3" style={{ color: DD.accent }}>
                  Drag & Drop Visuals Here
                </p>
                <div className="flex gap-2">
                  <button
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                    style={{ background: "rgba(255,255,255,0.06)", color: DD.text, border: `1px solid ${DD.border}` }}
                    onClick={e => { e.stopPropagation(); fileInputRef.current?.click(); }}
                    data-testid="button-upload-image"
                  >
                    <Upload className="w-3.5 h-3.5" /> Upload Image
                  </button>
                  <button
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                    style={{ background: "rgba(255,255,255,0.06)", color: DD.text, border: `1px solid ${DD.border}` }}
                    onClick={e => { e.stopPropagation(); handleGenerateImage(); }}
                    disabled={!content.trim()}
                    data-testid="button-generate-image"
                  >
                    <Sparkles className="w-3.5 h-3.5" /> Generate with AI
                  </button>
                </div>
                <div
                  className="mt-4 mx-6 rounded-lg px-3 py-2.5 text-left"
                  style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${DD.border}` }}
                  data-testid="hint-employee-photos"
                >
                  <p className="text-[11px] font-semibold mb-1" style={{ color: DD.text }}>
                    Pro tip: upload real photos of your team
                  </p>
                  <p className="text-[11px] leading-relaxed" style={{ color: DD.textSecondary }}>
                    For the most realistic AI-generated images, upload clear, well-lit photos of your
                    actual employees — front-facing, full face visible, in uniform or work attire.
                    The more reference photos you provide (different angles, lighting, and outfits),
                    the more accurately the AI can recreate them in branded post visuals.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest block mb-1.5" style={{ color: DD.textSecondary }}>Title (optional)</label>
            <Input
              placeholder="Post title…"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="text-sm h-10"
              style={{ background: "#0a0e16", border: `1px solid ${DD.border}`, color: DD.text }}
              data-testid="input-post-title"
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: DD.textSecondary }}>Post Content</label>
              <span className="text-[10px] font-mono" style={{ color: charCount > maxChars ? DD.danger : DD.textTertiary }} data-testid="text-char-count">{charCount}/{maxChars}</span>
            </div>
            <Textarea
              placeholder="Write your post… Include target keywords for better ranking."
              value={content}
              onChange={e => setContent(e.target.value)}
              className="min-h-[120px] resize-none text-sm leading-relaxed"
              style={{ background: "#0a0e16", border: `1px solid ${DD.border}`, color: DD.text }}
              maxLength={maxChars}
              data-testid="input-post-content"
            />
            <div className="flex items-center gap-2 mt-2">
              <span className="text-[10px]" style={{ color: DD.textTertiary }}>Keyword Score:</span>
              <div className="w-14 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(kwScore, 4)}%`, background: kwScore >= 60 ? DD.green : DD.accent }} />
              </div>
              <span className="text-[11px] font-bold font-mono" style={{ color: kwScore >= 60 ? DD.green : DD.accent }} data-testid="text-kw-score">{kwScore}%</span>
            </div>
          </div>

          {editPost?.imagePrompt && (
            <div className="px-3 py-2 rounded-lg" style={{ background: "rgba(249,115,22,0.05)", border: `1px solid ${DD.accentBorder}` }}>
              <div className="text-[10px] font-semibold text-orange-400 uppercase tracking-wider mb-1">Preemptive Frame</div>
              <p className="text-xs text-white/50">{editPost.imagePrompt}</p>
            </div>
          )}

          {entityProps && Object.keys(entityProps).length > 0 && (
            <div>
              <label className="text-[10px] font-semibold text-white/50 uppercase tracking-widest block mb-1.5">Entity Properties</label>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(entityProps).map(([key, val]) => (
                  <Badge key={key} variant="outline" className="text-[10px]" style={{ color: DD.purple, borderColor: "rgba(139,92,246,0.2)", background: "rgba(139,92,246,0.05)" }}>
                    {key}: {String(val)}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {detectedKws.length > 0 && (
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest block mb-1.5" style={{ color: DD.textSecondary }}>Keywords Detected</label>
              <div className="flex flex-wrap gap-1.5">
                {detectedKws.map((k, i) => (
                  <span key={i} className="px-2 py-0.5 rounded text-[11px] bg-orange-500/10 text-orange-400 border border-orange-500/20">
                    {k.keyword} <span className="text-orange-500/60">#{k.avgRank?.toFixed(1) || "—"}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {suggestedKws.length > 0 && detectedKws.length < 3 && (
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest block mb-1.5" style={{ color: DD.textSecondary }}>Suggested Keywords</label>
              <div className="flex flex-wrap gap-1.5">
                {suggestedKws.map((k, i) => (
                  <button key={i} className="px-2 py-0.5 rounded text-[11px] transition-colors hover:brightness-125" style={{ background: DD.surface, color: DD.textSecondary, border: `1px solid ${DD.border}` }} onClick={() => setContent(prev => prev + ` ${k.keyword}`)} data-testid={`button-suggest-kw-${i}`}>
                    + {k.keyword}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-[10px] font-semibold uppercase tracking-widest block mb-1.5" style={{ color: DD.textSecondary }}>Date</label>
              <Input type="date" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)} className="h-10" style={{ background: "#0a0e16", border: `1px solid ${DD.border}`, color: DD.text }} data-testid="input-post-date" />
            </div>
            <div className="flex-1">
              <label className="text-[10px] font-semibold uppercase tracking-widest block mb-1.5" style={{ color: DD.textSecondary }}>Time</label>
              <Input type="time" value={scheduledTime} onChange={e => setScheduledTime(e.target.value)} className="h-10" style={{ background: "#0a0e16", border: `1px solid ${DD.border}`, color: DD.text }} data-testid="input-post-time" />
            </div>
          </div>

          {postType === "offer" && (
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest block mb-1.5" style={{ color: DD.textSecondary }}>Coupon Code</label>
              <Input placeholder="e.g. SPRING15" value={couponCode} onChange={e => setCouponCode(e.target.value)} className="h-10" style={{ background: "#0a0e16", border: `1px solid ${DD.border}`, color: DD.text }} data-testid="input-coupon-code" />
            </div>
          )}

          {postType === "event" && (
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-[10px] font-semibold uppercase tracking-widest block mb-1.5" style={{ color: DD.textSecondary }}>Event Start</label>
                <Input type="date" value={eventStartDate} onChange={e => setEventStartDate(e.target.value)} className="h-10" style={{ background: "#0a0e16", border: `1px solid ${DD.border}`, color: DD.text }} data-testid="input-event-start" />
              </div>
              <div className="flex-1">
                <label className="text-[10px] font-semibold uppercase tracking-widest block mb-1.5" style={{ color: DD.textSecondary }}>Event End</label>
                <Input type="date" value={eventEndDate} onChange={e => setEventEndDate(e.target.value)} className="h-10" style={{ background: "#0a0e16", border: `1px solid ${DD.border}`, color: DD.text }} data-testid="input-event-end" />
              </div>
            </div>
          )}

          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest block mb-1.5" style={{ color: DD.textSecondary }}>Call to Action</label>
            <Select value={ctaType} onValueChange={setCtaType}>
              <SelectTrigger className="h-10" style={{ background: "#0a0e16", border: `1px solid ${DD.border}` }} data-testid="select-cta-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[110]">
                {CTA_TYPES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {ctaType && ctaType !== "none" && ctaType !== "CALL" && (
              <Input type="url" placeholder="https://…" value={ctaUrl} onChange={e => setCtaUrl(e.target.value)} className="h-10 mt-2" style={{ background: "#0a0e16", border: `1px solid ${DD.border}`, color: DD.text }} data-testid="input-cta-url" />
            )}
          </div>

          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest block mb-1.5" style={{ color: DD.textSecondary }}>Tags</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {tags.map((tag, i) => (
                <span key={i} className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                  {tag}
                  <button className="hover:text-white ml-0.5" onClick={() => setTags(tags.filter((_, j) => j !== i))} data-testid={`button-remove-tag-${i}`}>×</button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Add a tag…"
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && tagInput.trim()) {
                    e.preventDefault();
                    const newTag = tagInput.trim().toLowerCase();
                    if (!tags.includes(newTag)) setTags([...tags, newTag]);
                    setTagInput("");
                  }
                }}
                className="text-xs flex-1 h-10"
                style={{ background: "#0a0e16", border: `1px solid ${DD.border}`, color: DD.text }}
                data-testid="input-tag"
              />
              <Button variant="outline" size="sm" className="text-xs h-10 px-3" style={{ border: `1px solid ${DD.border}`, color: DD.textSecondary }} onClick={() => {
                if (tagInput.trim()) {
                  const newTag = tagInput.trim().toLowerCase();
                  if (!tags.includes(newTag)) setTags([...tags, newTag]);
                  setTagInput("");
                }
              }} data-testid="button-add-tag">
                <Tag className="w-3 h-3 mr-1" /> Add
              </Button>
            </div>
          </div>

          {competitors.length > 0 && (
            <div className="rounded-xl" style={{ background: DD.surface, border: `1px solid ${DD.border}` }}>
              <button
                className="w-full flex items-center justify-between p-3 text-left"
                onClick={() => setShowCompetitorIntel(!showCompetitorIntel)}
                data-testid="button-toggle-competitor-intel"
              >
                <span className="flex items-center gap-2 text-xs font-semibold text-white/70">
                  <Swords className="w-3.5 h-3.5 text-red-400" />
                  Competitor Intelligence ({competitors.length})
                </span>
                {showCompetitorIntel ? <ChevronUp className="w-3 h-3 text-white/30" /> : <ChevronDown className="w-3 h-3 text-white/30" />}
              </button>
              {showCompetitorIntel && (
                <div className="px-3 pb-3 space-y-1.5">
                  {competitors.map((comp: CompetitorInfo, i: number) => (
                    <div key={i} className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-black/20 border border-white/5">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-white/70 font-medium truncate">{comp.competitorName}</div>
                        <div className="text-[10px] text-white/30">{comp.postsThisWeek || 0} posts this week</div>
                      </div>
                      <Badge variant="outline" className="text-[9px] border-none px-1.5" style={{ color: comp.threatLevel === "high" ? DD.danger : comp.threatLevel === "medium" ? DD.warning : DD.green, background: `${comp.threatLevel === "high" ? DD.danger : comp.threatLevel === "medium" ? DD.warning : DD.green}15` }}>
                        {comp.threatLevel}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest block mb-2" style={{ color: DD.textSecondary }}>Optimization Tips</label>
            <div className="space-y-1.5">
              {optimizationTips.map((tip, i) => (
                <div key={i} className={`px-3 py-1.5 rounded-lg text-[11px] font-medium ${tip.good ? "bg-green-500/8 text-green-400 border border-green-500/15" : "bg-amber-500/8 text-amber-400 border border-amber-500/15"}`} data-testid={`tip-${i}`}>
                  {tip.text}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-5 py-3 flex items-center gap-2 flex-shrink-0" style={{ borderTop: `1px solid ${DD.border}`, background: "#0a0e16" }}>
          {editPost && (
            <button className="flex items-center gap-1 text-xs font-semibold transition-colors hover:text-red-400 mr-auto" style={{ color: DD.danger }} onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending} data-testid="button-delete-post">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          )}
          {!editPost && <div className="mr-auto" />}
          <button className="text-xs px-3 py-2 rounded-lg transition-colors hover:bg-white/5" style={{ color: DD.textSecondary }} onClick={() => saveMutation.mutate("draft")} disabled={!content.trim() || saveMutation.isPending} data-testid="button-save-draft">
            Save Draft
          </button>
          <Button size="sm" className="text-white font-semibold h-8 px-4" style={{ background: DD.accent }} onClick={() => saveMutation.mutate("scheduled")} disabled={!content.trim() || saveMutation.isPending} data-testid="button-schedule-post">
            <Clock className="w-3 h-3 mr-1" /> Schedule
          </Button>
          <Button size="sm" className="text-white font-semibold h-8 px-4" style={{ background: DD.purple }} onClick={() => saveMutation.mutate("published")} disabled={!content.trim() || saveMutation.isPending} data-testid="button-publish-post">
            {saveMutation.isPending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
            Publish Now
          </Button>
        </div>
      </div>
    </div>
  );
}
